import { gql, useSubscription, useMutation } from '@apollo/client'
import { NotificationPresenter } from 'shared/presenters/NotificationPresenter'
import React, { createContext, ReactNode, useEffect, useState } from 'react'
import { Notifications } from 'shared/presenters/graphqlTypes'
import { HttpHeader, XForceRole } from 'shared/enums/httpHeader'
import { useInstructor } from 'shared/state/useAuth'
import { mapPresenter } from 'shared/presenters'

export interface NotificationsProviderValue {
  notifications: NotificationPresenter[]
  useNotificationWatch: () => void
  notificationFetchLoading: boolean
  canExtendToRevealMoreNotifications: boolean
  extendNotificationLimit: () => void
}

export const NotificationsContext = createContext<NotificationsProviderValue>({
  notifications: [],
  notificationFetchLoading: false,
  useNotificationWatch: () => {
    throw new Error(
      'useNotificationWatch must be used within NotificationsProvider'
    )
  },
  canExtendToRevealMoreNotifications: false,
  extendNotificationLimit: () => {
    throw new Error(
      'extendNotificationLimit must be used within NotificationsProvider'
    )
  }
})

type NotificationsSubsriptionData = {
  notifications: Notifications[]
}

export const INITIAL_NOTIFICATIONS_LIMIT = 20
export const LIMIT_INCREASE = 20
export const TIME_UNTIL_READ_UPDATE = 3000

const NotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { instructor, authLoading } = useInstructor()
  const [notificationLimit, setNotificationLimit] = useState(
    INITIAL_NOTIFICATIONS_LIMIT
  )
  const [notifications, setNotifications] = useState<NotificationPresenter[]>(
    []
  )

  const { data, loading: notificationFetchLoading } =
    useSubscription<NotificationsSubsriptionData>(FETCH_NOTIFICATIONS, {
      context: { headers: { [HttpHeader.xForceRole]: XForceRole.instructor } },
      variables: {
        notificationLimit: notificationLimit + 1,
        instructorId: instructor?.id
      },
      skip: authLoading,
      onData: ({ data }) => {
        /* Store the notifications in local state, to avoid the notifications being
      cleared whilst the next fetch is loading */
        setNotifications(
          mapPresenter(NotificationPresenter, data?.data?.notifications ?? [])
        )
      }
    })

  const [updateAllNotificationsToRead] = useMutation(
    UPDATE_NOTIFICATIONS_TO_READ,
    {
      context: { headers: { [HttpHeader.xForceRole]: XForceRole.instructor } },
      variables: { instructorId: instructor?.id }
    }
  )

  const maxNumberOfNotifications = notifications?.length
  const canExtendToRevealMoreNotifications =
    notificationLimit < maxNumberOfNotifications

  const useNotificationWatch = () =>
    useNotificationWatchHook(notifications, updateAllNotificationsToRead, data)

  // NOTE: this loads ALL notifications again with an increased limit -
  // it would be more efficiont to add an offset to the query and append state
  // but then notification read state wouldn't update via subscription.
  const extendNotificationLimit = () =>
    setNotificationLimit(notificationLimit + LIMIT_INCREASE)

  return (
    <NotificationsContext.Provider
      value={{
        notifications: notifications.slice(0, notificationLimit),
        notificationFetchLoading,
        useNotificationWatch,
        canExtendToRevealMoreNotifications,
        extendNotificationLimit
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

export default NotificationsProvider

const FETCH_NOTIFICATIONS = gql`
  subscription BusinessNotificationsProviderFetchNotifications(
    $notificationLimit: Int!
    $instructorId: uuid!
  ) {
    notifications(
      where: { user_id: { _eq: $instructorId } }
      order_by: { updated_at: desc }
      limit: $notificationLimit
    ) {
      id
      message
      created_at
      kind
      read
      cta_link
    }
  }
`
const useNotificationWatchHook = (
  notifications: NotificationPresenter[],
  updateAllNotificationsToRead: () => void,
  data?: NotificationsSubsriptionData
) => {
  useEffect(() => {
    /** if the first notification is read then it means they are all read */
    if (!notifications?.length || notifications?.[0].read) {
      return
    }

    const timeoutId = setTimeout(() => {
      updateAllNotificationsToRead()
    }, TIME_UNTIL_READ_UPDATE)

    return () => {
      clearTimeout(timeoutId)
    }
    // TODO #6367 - Fix ESLint - hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])
}

const UPDATE_NOTIFICATIONS_TO_READ = gql`
  mutation BusinessUpdateNotificationsToRead($instructorId: uuid!) {
    update_notifications(
      where: { user_id: { _eq: $instructorId }, read: { _eq: false } }
      _set: { read: true }
    ) {
      affected_rows
    }
  }
`
